<template>
  <div class="page-container">
    <div class="stat-row">
      <div class="stat-card" v-for="s in zstats" :key="s.label" :style="{ borderLeft: '4px solid ' + (s.color || '#165dff') }">
        <div class="sc-num" :style="{ color: s.color || '#165dff' }">{{ s.value }}</div>
        <div class="sc-label">{{ s.label }}</div>
      </div>
    </div>

    <div class="search-box">
      <span class="sb-title">限速区域</span>
      <el-button v-if="hasPermission('security_add')" type="success" icon="el-icon-plus" @click="openAdd">新增区域</el-button>
    </div>

    <div class="table-box">
      <el-table :data="tableData" border v-loading="loading" empty-text="暂无限速区域" height="100%">
        <el-table-column prop="zone" label="区域名称" min-width="160" />
        <el-table-column label="限速值" min-width="110">
          <template slot-scope="scope">{{ scope.row.speed }} km/h</template>
        </el-table-column>
        <el-table-column label="生效时段" min-width="140">
          <template slot-scope="scope">{{ scope.row.time || '全天' }}</template>
        </el-table-column>
        <el-table-column label="覆盖设备" min-width="90">
          <template slot-scope="scope">{{ scope.row.forklifts }} 台</template>
        </el-table-column>
        <el-table-column label="状态" min-width="90">
          <template slot-scope="scope">
            <el-tag :type="scope.row.active ? 'success' : 'info'" size="mini">{{ scope.row.active ? '启用' : '停用' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="边界点" min-width="90">
          <template slot-scope="scope">{{ (scope.row.polygon || []).length }} 点</template>
        </el-table-column>
        <el-table-column label="操作" width="160" fixed="right">
          <template slot-scope="scope">
            <el-link v-if="hasPermission('security_edit')" type="primary" @click="openEdit(scope.row)">编辑</el-link>
            <el-link v-if="hasPermission('security_delete')" type="danger" @click="removeRow(scope.row)">删除</el-link>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-drawer
      :title="drawerTitle"
      :visible.sync="drawer"
      direction="rtl"
      size="70%"
      custom-class="security-drawer"
      :wrapper-closable="false"
      @opened="onDrawerOpened"
      @close="resetForm">
      <div class="drawer-body">
        <div class="form-col">
          <el-form label-width="90px" label-position="right">
            <el-form-item label="区域名称">
              <el-input v-model="form.name" placeholder="请输入区域名称" />
            </el-form-item>
            <el-form-item label="限速值">
              <el-input v-model="form.speed" placeholder="如 15" style="width:160px">
                <template slot="append">km/h</template>
              </el-input>
            </el-form-item>
            <el-form-item label="生效时段">
              <el-input v-model="form.time_range" placeholder="留空表示每天全天生效" style="width:260px" />
            </el-form-item>
          </el-form>
        </div>
        <div class="map-col">
          <div class="map-title">
            <span>围栏圈选</span>
            <span class="zone-tip">已选 {{ (form.polygon || []).length }} 个边界点</span>
          </div>
          <TMapPicker ref="tmap" mode="zone" :center="mapCenter" :value="form.polygon" @change="onZone" :height="520" />
        </div>
      </div>
      <div class="drawer-foot">
        <el-button @click="drawer = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存</el-button>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import TMapPicker from '@/views/components/TMapPicker.vue'
import { getSecurity, createSpeedZone, updateSpeedZone, deleteSpeedZone } from '@/api/sentinel'

export default {
  name: 'SecurityIndex',
  components: { TMapPicker },
  data() {
    return {
      // 后端 zstats 是数组：[{ value, label, icon, color, bg }]
      zstats: [],
      tableData: [],
      loading: false,
      drawer: false,
      drawerTitle: '新增限速区域',
      saving: false,
      // 后端 rules 字段为 zone/time/forklifts/active，polygon 用 {latitude,longitude}
      form: { id: 0, name: '', speed: '', time_range: '', polygon: [] },
      mapCenter: { lat: 30.2741, lng: 120.1551 }
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    hasPermission(k) {
      return this.$store.getters.hasPermission(k)
    },
    loadData() {
      this.loading = true
      getSecurity().then(res => {
        if (res.code === 200) {
          const d = res.data || {}
          // zstats：后端返回数组，直接用于顶部统计卡
          this.zstats = d.zstats || []
          // rules：字段名 zone/time/forklifts/active，polygon 为 [{latitude,longitude}]
          this.tableData = (d.rules || []).map(r => ({
            ...r,
            zone: r.zone || '',
            speed: r.speed || '0',
            time: r.time || '全天',
            forklifts: r.forklifts || 0,
            active: !!r.active,
            polygon: r.polygon || []
          }))
        }
      }).finally(() => { this.loading = false })
    },
    openAdd() {
      this.drawerTitle = '新增限速区域'
      this.form = { id: 0, name: '', speed: '', time_range: '', polygon: [] }
      this.drawer = true
    },
    openEdit(row) {
      this.drawerTitle = '编辑限速区域'
      this.form = {
        id: row.id,
        name: row.zone,
        speed: row.speed,
        time_range: row.time || '',
        // 后端 polygon 为 {latitude,longitude}，TMapPicker 用 {lat,lng}，转换之
        polygon: (row.polygon || []).map(p => ({ lat: +p.latitude, lng: +p.longitude }))
      }
      // 中心点：优先用后端返回的中心，否则取第一个顶点
      const c = row.center
      if (c && (c.latitude || c.longitude)) {
        this.mapCenter = { lat: +c.latitude, lng: +c.longitude }
      } else if (this.form.polygon.length) {
        this.mapCenter = { lat: this.form.polygon[0].lat, lng: this.form.polygon[0].lng }
      }
      this.drawer = true
    },
    onZone(arr) {
      this.form.polygon = arr || []
    },
    resetForm() {
      this.form = { id: 0, name: '', speed: '', time_range: '', polygon: [] }
    },
    onDrawerOpened() {
      // 抽屉滑入动画结束后，让地图按最终尺寸重算，消除点击偏移
      if (this.$refs.tmap && this.$refs.tmap.resizeMap) {
        this.$refs.tmap.resizeMap()
        setTimeout(() => this.$refs.tmap.resizeMap(), 200)
      }
    },
    save() {
      if (!this.form.name) return this.$message.warning('请输入区域名称')
      if (!this.form.speed) return this.$message.warning('请输入限速值')
      if (!this.form.polygon || this.form.polygon.length < 3) return this.$message.warning('请圈选至少 3 个边界点')
      // 后端入参名 speed_limit（兼容 speed）；polygon 用 {lat,lng}，后端双格式兼容
      const payload = {
        name: this.form.name,
        speed_limit: +this.form.speed,
        time_range: this.form.time_range || undefined,
        polygon: this.form.polygon
      }
      this.saving = true
      const req = this.form.id ? updateSpeedZone(this.form.id, payload) : createSpeedZone(payload)
      req.then(res => {
        if (res.code === 200) {
          this.$message.success('保存成功')
          this.drawer = false
          this.loadData()
        }
      }).finally(() => { this.saving = false })
    },
    removeRow(row) {
      this.$confirm(`确认删除限速区域「${row.zone}」？`, '提示', { type: 'warning' }).then(() => {
        deleteSpeedZone(row.id).then(res => {
          if (res.code === 200) {
            this.$message.success('已删除')
            this.loadData()
          }
        })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.stat-row { display: flex; gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
.stat-card { background: #fff; border-radius: 4px; box-shadow: 0 2px 12px rgba(0,0,0,.1); padding: 16px 24px; min-width: 160px; }
.sc-num { font-size: 28px; font-weight: 700; }
.sc-label { font-size: 13px; color: #86909c; margin-top: 4px; }
.search-box { display: flex; align-items: center; justify-content: space-between; background: #fff; padding: 12px 16px; border-radius: 4px; box-shadow: 0 2px 12px rgba(0,0,0,.1); margin-bottom: 16px; }
.sb-title { font-size: 15px; font-weight: 600; color: #1D2129; }
.table-box { background: #fff; padding: 16px; border-radius: 4px; box-shadow: 0 2px 12px rgba(0,0,0,.1); }
.security-drawer >>> .el-drawer__body {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0;
  overflow: hidden;
}
.drawer-body {
  flex: 1;
  display: flex;
  min-height: 0;
  overflow: hidden;
}
.form-col {
  width: 360px;
  padding: 24px;
  overflow-y: auto;
  border-right: 1px solid #E5E6EB;
  background: #fff;
}
.map-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px;
  min-width: 0;
  background: #fff;
}
.map-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #1D2129;
}
.zone-tip { font-size: 12px; color: #86909c; font-weight: normal; }
.drawer-foot {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #E5E6EB;
  background: #fff;
}
@media screen and (max-width: 992px) {
  .drawer-body { flex-direction: column; }
  .form-col { width: auto; border-right: none; border-bottom: 1px solid #E5E6EB; }
}
</style>
